import type { ErrorBody, ErrorDetail } from '@cusp/contracts';
import { ApiError } from './apiError.js';
import { getAccessToken, refreshAccessToken } from './accessToken.js';
import { getApiBaseUrl } from './config.js';
import { authHeaders, authHeadersFor, hasTransientIdentity, type Identity } from './identity.js';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** react-query hands one to every queryFn — thread it so cancelQueries works. */
  signal?: AbortSignal;
  /**
   * The request IS a credential exchange: its input authenticates itself, so a
   * 401 is the answer, not a stale access token — retrying behind a refresh
   * would turn "this credential is no good" into a refresh round-trip (or a
   * loop). Set by login, refresh and every verifier route (PR #93 review:
   * metadata on the call, not a path list in here, so the NEXT verifier
   * cannot forget to extend a list it has never heard of).
   */
  credentialExchange?: boolean;
  /**
   * Let this request outlive the document that started it. `fetch` cancels
   * in-flight requests when the page goes away, so the one write that most
   * needs to land — a media player's final position, sent from `pagehide` — is
   * the one most likely to be thrown away without it.
   *
   * For small, fire-and-forget writes only: browsers cap the total keepalive
   * body across a page at 64KB, and a keepalive request cannot be retried
   * behind a refresh because the page it would retry into is gone.
   */
  keepalive?: boolean;
  /**
   * Send as this membership rather than the selected one. For a write QUEUED
   * under one identity and sent after the picker has moved on — the engagement
   * beacon's final flush — where the current identity would file it under the
   * institution the learner just switched to.
   */
  identity?: Identity;
  /** Internal: set on the retry so a refresh loop cannot recurse. */
  retried?: boolean;
}

/**
 * The one place a fetch happens. Hand-rolled over `fetch`, no axios.
 *
 * Three deliberate deltas from the blueberry original:
 *  - No `ApiResponse` envelope. cusp returns the payload bare on success and
 *    `{ error: { code, message, requestId } }` on failure, so there is no
 *    `body.ok` to unwrap.
 *  - Identity is a header pair, injected here so no caller can forget it: the
 *    bearer ACCESS TOKEN says who you are, `x-tenant-id` says which of your
 *    memberships you are acting as. Both are verified server-side; the tenant
 *    half is a claim proven against `memberships` on every request.
 *  - `credentials` is left at fetch's default (`same-origin`), which is exactly
 *    what the refresh cookie needs and no more. The access token travels in a
 *    header precisely so that cross-origin and native clients need no cookie.
 *
 * A 401 is retried ONCE, behind a refresh (issue #45). Access tokens live for
 * minutes, so without this every screen would flash an auth error several times
 * an hour; with it, expiry is invisible to callers and the only thing that
 * surfaces a 401 is a session that is genuinely over.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    signal,
    credentialExchange = false,
    keepalive = false,
    identity,
    retried = false,
  } = opts;

  const headers: Record<string, string> =
    identity === undefined ? { ...authHeaders() } : { ...authHeadersFor(identity) };
  const token = getAccessToken();
  if (token !== null) headers['authorization'] = `Bearer ${token}`;

  const init: RequestInit = { method, headers };
  if (keepalive) init.keepalive = true;
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (signal) init.signal = signal;

  const res = await fetch(`${getApiBaseUrl()}${path}`, init);

  // Demo requests authenticate through the transport's prospect headers. A
  // 401 there is the API's answer; the browser's real refresh cookie is not
  // part of that identity and must not be consulted.
  if (res.status === 401 && !retried && !credentialExchange && !hasTransientIdentity()) {
    const renewed = await refreshAccessToken();
    if (renewed !== null) return request<T>(path, { ...opts, retried: true });
  }

  if (!res.ok) throw await toApiError(res, path);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * `details` is carried through, and it is VALIDATED rather than trusted.
 *
 * Everything else here comes off a body this client did not build, and the
 * same is true of the array: a proxy, a gateway or an older API can send
 * anything under that name.
 *
 * ALL OR NOTHING, and that is the deliberate half. Filtering the bad entries
 * out and keeping the good ones looks more forgiving and is less safe: a body
 * that does not satisfy `ErrorDetail[]` is a body this client has no reason to
 * trust ANY of, and screens use these keys to choose which sentence an
 * administrator reads. Salvaging one entry out of a malformed envelope means
 * confidently rendering business copy off a payload that failed its own
 * contract. So one bad member discards the array, and `undefined` — not `[]` —
 * is the answer: "the server said nothing" and "the server said something this
 * client could not read" are different facts, and a caller told the second is
 * the first will report a refusal it cannot actually name.
 *
 * Empty strings are rejected with the rest. `{ path: '', message: '' }` is two
 * strings and no information, and a reason nobody can match on is not a reason.
 */
function toDetails(raw: unknown): readonly ErrorDetail[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const ok = raw.every(
    (d) =>
      typeof d === 'object' &&
      d !== null &&
      typeof (d as ErrorDetail).path === 'string' &&
      (d as ErrorDetail).path.length > 0 &&
      typeof (d as ErrorDetail).message === 'string' &&
      (d as ErrorDetail).message.length > 0,
  );
  return ok ? (raw as ErrorDetail[]) : undefined;
}

async function toApiError(res: Response, path: string): Promise<ApiError> {
  const fallback = `HTTP ${res.status} on ${path}`;
  try {
    const body = (await res.json()) as Partial<ErrorBody>;
    const err = body.error;
    if (err && typeof err.message === 'string' && err.message.length > 0) {
      return new ApiError(
        err.message,
        res.status,
        path,
        err.code,
        err.requestId,
        toDetails(err.details),
      );
    }
  } catch {
    // Non-JSON body (proxy error page, gateway timeout) — fall through.
  }
  return new ApiError(fallback, res.status, path);
}
